import React from 'react';
import { usePos } from '../PosContext';
import { HandPlatter, DollarSign, Trash2, Split, MessageSquare, Printer } from 'lucide-react';
import FulfillmentSelector from '../../../components/FulfillmentSelector';
import { CustomerFields } from '../../../features/orders/CustomerFields';
import { ItemRow } from '../../../components/POS/ItemRow';
import { OrderSummaryFooter } from '../../../components/POS/OrderSummaryFooter';
import { SplitOrderModal } from '../../../components/POS/SplitOrderModal';
import { ConfirmCancelOrderModal } from '../../../features/orders/components/ConfirmCancelOrderModal';
import type { Order, FulfillmentType, ServeMode } from '../../../types/@types/order';
import type { Table } from '../../../types/@types/table';

interface CartItem {
  product: any;
  qty: number;
  flavors?: { id: string; name: string; qty: number }[];
  additions?: Record<string, number>;
  exceptions?: string[];
  linePriceCalc?: any;
  kitchenSummary?: string;
  serveMode: ServeMode;
}

export interface CartPanelProps {
  isTakeawayMode: boolean;
  selected: Table | null;
  orderCustomer: { customerName: string; customerPhone: string };
  setSelected: (val: Table | null) => void;
  hasActiveOrder: boolean;
  activeOrder: Order | null;
  draftFulfillment: FulfillmentType;
  handleOrderFulfillmentChange: (next: FulfillmentType) => void;
  orderLocked: boolean;
  previousOrdersWithItems: Array<{ order: Order; items: any[]; totals: any; sequence: number }>;
  ORDER_STATUS_LABELS: Record<string, string>;
  buildDetailContentFromOrderItem: (item: any) => React.ReactNode;
  activeOrderSequence: number | null;
  setSplitModalOpen: (val: boolean) => void;
  splittingOrder: boolean;
  setCancelModalOpen: (val: boolean) => void;
  cancelOrderIsPending: boolean;
  ordersByTable: Order[] | undefined;
  setActiveOrderId: (id: string | number | null) => void;
  activeOrderId: string | number | null;
  activeOrderItems: any[];
  ITEM_STATUS_LABELS: Record<string, string>;
  ITEM_STATUS_CLASSES: Record<string, string>;
  openOrderItem: string | null;
  setOpenOrderItem: (id: string | null) => void;
  handleActiveItemFulfillment: (id: string, mode: FulfillmentType | null) => void;
  totals: { subtotal: number; total: number } | null;
  handleSendPending: () => void;
  hasPendingItems: boolean;
  sendToKitchenMutationIsPending: boolean;
  pendingItemsCount: number;
  handlePayClick: () => void;
  hasDraft: boolean;
  nextOrderSequence: number;
  highestKnownSequence: number;
  cart: Record<string, CartItem>;
  handleCartItemServeMode: (key: string, mode: ServeMode) => void;
  setOptionsKey: (key: string) => void;
  buildDetailContentFromCartItem: (item: CartItem) => React.ReactNode;
  changeQty: (key: string, delta: number) => void;
  removeCartItem: (key: string) => void;
  setCart: (val: any) => void;
  handleOrder: () => void;
  hasFlavorErrors: boolean;
}

export function CartPanel() {
  const {
    isTakeawayMode, selectedTable: selected, orderCustomer, setSelectedTable: setSelected,
    hasActiveOrder, activeOrder, draftFulfillment, handleOrderFulfillmentChange,
    orderLocked, previousOrdersWithItems, activeOrderSequence,
    splittingOrder, cancelOrderIsPending, ordersByTable,
    setActiveOrderId, activeOrderId, activeOrderItems,
    totals, handleSendPending, sendToKitchenMutationIsPending,
    pendingItemsCount, hasPendingItems,
    handlePayClick, hasDraft, nextOrderSequence, highestKnownSequence,
    cart, handleCartItemServeMode, setOptionsKey,
    buildDetailContentFromCartItem, buildDetailContentFromOrderItem,
    changeQty, removeCartItem, setCart,
    handleOrder, hasFlavorErrors,
    createOrderIsPending, handleActiveItemFulfillment,
    lastReceipt, setReceiptToPrint,
    splitOrderMutation, cancelOrderMutation, handleCancelActiveOrder,
    removeActiveOrderItem, removingItem, printOrderTicket,
  } = usePos();

  // Mapping constant labels that were probably passed as props
  const ORDER_STATUS_LABELS: any = { WAITING: 'En espera', IN_PRODUCTION: 'En cocina', DONE: 'Listo', DELIVERED: 'Entregado', PAID: 'Pagado', CANCELLED: 'Cancelado' };
  const ITEM_STATUS_LABELS: any = { PENDING: 'Pendiente', WAITING: 'En cola', SENT_TO_KITCHEN: 'En cocina', IN_PRODUCTION: 'Preparando', DONE: 'Listo', DELIVERED: 'Entregado', CANCELLED: 'Cancelado' };
  const ITEM_STATUS_CLASSES: any = { PENDING: 'bg-yellow-100 text-yellow-700', WAITING: 'bg-orange-100 text-orange-700', SENT_TO_KITCHEN: 'bg-blue-100 text-blue-700', IN_PRODUCTION: 'bg-purple-100 text-purple-700', DONE: 'bg-green-100 text-green-700', DELIVERED: 'bg-teal-100 text-teal-700', CANCELLED: 'bg-red-100 text-red-700' };

  const [splitModalOpen, setSplitModalOpen] = React.useState(false);
  const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
  const [openOrderItem, setOpenOrderItem] = React.useState<string | null>(null);
  const [openNoteKey, setOpenNoteKey] = React.useState<string | null>(null);

  const setItemNote = (key: string, note: string) => {
    setCart((prev: any) => ({
      ...prev,
      [key]: { ...prev[key], note },
    }));
  };

  const trimmedCustomerName = orderCustomer.customerName.trim();

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-divider bg-surface px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[color:var(--color-text)]">
              {isTakeawayMode ? 'Pedido para llevar' : `Mesa #${selected?.number}`}
            </p>
            {isTakeawayMode ? (
              trimmedCustomerName ? (
                <p className="text-xs text-muted">Cliente: {trimmedCustomerName}</p>
              ) : null
            ) : (
              <p className="text-xs text-muted">ID: {selected?.id}</p>
            )}
          </div>
          {isTakeawayMode ? (
            <span className="inline-flex min-h-[2rem] items-center rounded-full bg-blue-100 px-3 text-xs font-semibold text-blue-700">
              TAKEAWAY
              {trimmedCustomerName ? ` • ${trimmedCustomerName}` : ''}
            </span>
          ) : (
            <button
              onClick={() => setSelected(null)}
              className="inline-flex h-10 items-center justify-center rounded-full border border-divider px-4 text-sm font-semibold text-[color:var(--color-text)] transition hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]/70"
              aria-label="Cambiar mesa"
            >
              Cambiar mesa
            </button>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-xs font-semibold text-muted">Modo de entrega</span>
          <FulfillmentSelector
            value={
              hasActiveOrder && activeOrder
                ? (activeOrder.fulfillmentType ?? 'DINE_IN')
                : draftFulfillment
            }
            onChange={handleOrderFulfillmentChange}
            disabled={hasActiveOrder ? orderLocked : false}
            compact
          />
        </div>
        <CustomerFields forceShow={
          (hasActiveOrder && activeOrder ? activeOrder.fulfillmentType : draftFulfillment) === 'TAKEOUT'
        } />
      </div>

      {hasActiveOrder ? (
        <div className="flex flex-col gap-4">
          {previousOrdersWithItems.length > 0 && (
            <div className="space-y-3">
              {previousOrdersWithItems.map(({ order, items, totals: previousTotals, sequence }) => (
                <article
                  key={order.id}
                  className="rounded-2xl border border-divider bg-surface-muted px-4 py-3 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[color:var(--color-text)]">Orden #{sequence}</p>
                    <span className="inline-flex min-h-[1.5rem] items-center rounded-full bg-surface px-3 text-[10px] font-semibold text-muted">
                      {ORDER_STATUS_LABELS[order.status] ?? order.status}
                    </span>
                  </div>
                  <ul className="mt-2 divide-y divide-[color:var(--color-border)]/60">
                    {items.map((item: any, index: number) => (
                      <li key={item.id ?? `${order.id}-${index}`} className="py-2">
                        <ItemRow
                          id={item.id ?? `${order.id}-${index}`}
                          quantity={item.quantity}
                          name={item.name || item.product?.name || 'Producto'}
                          unitPrice={item.unitPrice}
                          detailContent={buildDetailContentFromOrderItem(item)}
                        />
                      </li>
                    ))}
                  </ul>
                  <OrderSummaryFooter
                    subtotal={previousTotals.subtotal}
                    total={previousTotals.total}
                    className="mt-3"
                  />
                </article>
              ))}
            </div>
          )}

          <section className="space-y-3 rounded-2xl border border-divider bg-surface px-4 py-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[color:var(--color-text)]">
                {activeOrderSequence ? `Orden #${activeOrderSequence}` : 'Orden activa'}
              </h3>
              <div className="flex items-center gap-2">
                {activeOrder ? (
                  <>
                    <button
                      type="button"
                      onClick={() => printOrderTicket()}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-divider px-3 text-xs font-semibold text-[color:var(--color-text)] transition hover:bg-surface-muted"
                      aria-label="Imprimir comanda"
                      title="Imprimir comanda de cocina"
                    >
                      <Printer size={16} aria-hidden="true" />
                      Comanda
                    </button>
                    <button
                      type="button"
                      onClick={() => setSplitModalOpen(true)}
                      disabled={splittingOrder}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-divider px-3 text-xs font-semibold text-[color:var(--color-text)] transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Dividir cuenta"
                    >
                      <Split size={16} aria-hidden="true" />
                      Dividir
                    </button>
                    <button
                      type="button"
                      onClick={() => setCancelModalOpen(true)}
                      disabled={cancelOrderIsPending}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-divider px-3 text-xs font-semibold text-[color:var(--color-text)] transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Cancelar pedido"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                      Cancelar
                    </button>
                    <span className="inline-flex min-h-[1.5rem] items-center rounded-full bg-surface-muted px-3 text-[10px] font-semibold text-muted">
                      {ORDER_STATUS_LABELS[activeOrder.status.toUpperCase()] ?? activeOrder.status}
                    </span>
                  </>
                ) : (
                  <div className="flex items-center gap-2 animate-pulse">
                    <div className="h-6 w-16 rounded bg-muted" />
                    <div className="h-6 w-16 rounded bg-muted" />
                  </div>
                )}
              </div>
            </div>
            {ordersByTable && ordersByTable.length > 1 && (
              <div className="mb-4 flex flex-wrap gap-2 px-4">
                {ordersByTable.map((order, idx) => (
                  <button
                    key={order.id}
                    onClick={() => setActiveOrderId(order.id)}
                    className={`rounded-full px-3 py-1 text-xs font-bold transition ${
                      activeOrderId === order.id
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-muted text-text hover:bg-muted/80'
                    }`}
                  >
                    Cuenta {order.orderSequence || idx + 1}
                  </button>
                ))}
              </div>
            )}
            <ul className="divide-y divide-[color:var(--color-border)]/60" role="list">
              {activeOrderItems.map((item: any) => {
                const eff =
                  item.fulfillmentTypeOverride ??
                  (activeOrder?.fulfillmentType ?? 'DINE_IN');
                const statusKey =
                  typeof item.status === 'string'
                    ? item.status.toUpperCase()
                    : '';
                const statusLabel = statusKey
                  ? ITEM_STATUS_LABELS[statusKey] ?? item.rawStatus ?? statusKey
                  : typeof item.rawStatus === 'string'
                    ? item.rawStatus
                    : '';
                const statusClass = statusKey
                  ? ITEM_STATUS_CLASSES[statusKey] ?? 'bg-gray-200 text-gray-600'
                  : 'bg-gray-200 text-gray-600';
                return (
                  <li key={item.id} className="py-2">
                    <ItemRow
                      id={item.id}
                      quantity={item.quantity}
                      name={item.name || item.product?.name || 'Producto'}
                      unitPrice={item.unitPrice}
                      meta={[
                        statusLabel ? (
                          <span
                            key="status"
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass}`}
                          >
                            {statusLabel}
                          </span>
                        ) : null,
                        (
                          <div key="mode" className="relative">
                            <button
                              type="button"
                              disabled={orderLocked}
                              onClick={() =>
                                orderLocked
                                  ? undefined
                                  : openOrderItem === item.id
                                    ? setOpenOrderItem(null)
                                    : setOpenOrderItem(item.id)
                              }
                              className="inline-flex items-center rounded-full border border-divider px-3 py-1 text-[11px] font-medium text-[color:var(--color-text)] transition hover:bg-surface-muted disabled:opacity-50"
                              aria-label="Cambiar modo de servicio"
                              aria-expanded={!orderLocked && openOrderItem === item.id}
                            >
                              {eff === 'TAKEOUT' ? 'Para llevar' : 'En mesa'}
                            </button>
                            {!orderLocked && openOrderItem === item.id && (
                              <div className="absolute left-0 top-full z-20 mt-2 w-48 rounded-2xl border border-divider bg-surface text-xs shadow-elevated">
                                <button
                                  className="block w-full px-4 py-2 text-left hover:bg-surface-muted"
                                  onClick={() => {
                                    handleActiveItemFulfillment(item.id, 'TAKEOUT')
                                    setOpenOrderItem(null)
                                  }}
                                >
                                  Marcar como Para llevar
                                </button>
                                <button
                                  className="block w-full px-4 py-2 text-left hover:bg-surface-muted"
                                  onClick={() => {
                                    handleActiveItemFulfillment(item.id, 'DINE_IN')
                                    setOpenOrderItem(null)
                                  }}
                                >
                                  Marcar como En mesa
                                </button>
                                <button
                                  className="block w-full px-4 py-2 text-left hover:bg-surface-muted"
                                  onClick={() => {
                                    handleActiveItemFulfillment(item.id, null)
                                    setOpenOrderItem(null)
                                  }}
                                >
                                  Usar el de la orden
                                </button>
                              </div>
                            )}
                          </div>
                        ),
                        (!orderLocked && activeOrder?.payment_status !== 'PAID') ? (
                          <button
                            key="remove"
                            type="button"
                            disabled={removingItem}
                            onClick={() => {
                              if (confirm(`¿Quitar "${item.name || item.product?.name || 'este producto'}" del pedido?`)) {
                                removeActiveOrderItem(item.id);
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-[11px] font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                            aria-label="Quitar producto del pedido"
                            title="Quitar del pedido"
                          >
                            <Trash2 size={13} /> Quitar
                          </button>
                        ) : null,
                      ].filter(Boolean)}
                      detailContent={buildDetailContentFromOrderItem(item)}
                    />
                  </li>
                );
              })}
            </ul>
            {totals && (
              <OrderSummaryFooter
                subtotal={totals.subtotal}
                total={totals.total}
                className="mt-3"
              />
            )}
            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={handleSendPending}
                disabled={!hasPendingItems || sendToKitchenMutationIsPending}
                className="btn btn-primary w-full"
                aria-label="Enviar a cocina"
              >
                {sendToKitchenMutationIsPending
                  ? 'Enviando...'
                  : hasPendingItems
                    ? `Enviar a cocina (${pendingItemsCount})`
                    : 'Enviar a cocina'}
              </button>
              <button
                onClick={handlePayClick}
                disabled={
                  activeOrderItems.length === 0 ||
                  !activeOrder ||
                  activeOrder.payment_status === 'PAID' ||
                  activeOrder.status === 'CANCELLED'
                }
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-muted"
                aria-label="Cobrar orden"
              >
                <DollarSign size={18} aria-hidden="true" /> Cobrar
              </button>
              {lastReceipt && activeOrder?.payment_status === 'PAID' && (
                <button
                  type="button"
                  onClick={() => setReceiptToPrint(lastReceipt)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-divider px-4 text-xs font-semibold text-text-muted hover:bg-surface-muted transition"
                  aria-label="Reimprimir ticket"
                >
                  <Printer size={14} aria-hidden="true" /> Reimprimir
                </button>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {!hasActiveOrder && hasDraft ? (
        <section className="space-y-3 rounded-2xl border border-divider bg-surface px-4 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[color:var(--color-text)]">
              {`Nueva orden #${nextOrderSequence}`}
            </h3>
            {highestKnownSequence > 0 && (
              <span className="text-xs text-muted">
                Se enviará como ticket independiente
              </span>
            )}
          </div>
          <ul className="divide-y divide-[color:var(--color-border)]/60" role="list">
            {Object.entries(cart).map(([key, item]) => {
              const mode =
                item.serveMode ??
                (draftFulfillment === 'TAKEOUT' ? 'TAKEAWAY' : 'MESA');
              return (
                <li key={key} className="py-2">
                  <ItemRow
                    id={key}
                    quantity={item.qty}
                    name={item.product.name}
                    unitPrice={
                      item.qty > 0
                        ? (item.linePriceCalc?.final ?? item.product.price * item.qty) / item.qty
                        : item.product.price
                    }
                    meta={[
                      (
                        <div key="mode-toggle" className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleCartItemServeMode(key, 'MESA')}
                            className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${mode === 'MESA'
                              ? 'border-orange-500 bg-orange-500 text-white'
                              : 'border-divider text-[color:var(--color-text)] hover:bg-surface-muted'
                              }`}
                            aria-pressed={mode === 'MESA'}
                          >
                            En mesa
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCartItemServeMode(key, 'TAKEAWAY')}
                            className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${mode === 'TAKEAWAY'
                              ? 'border-blue-500 bg-blue-600 text-white'
                              : 'border-divider text-[color:var(--color-text)] hover:bg-surface-muted'
                              }`}
                            aria-pressed={mode === 'TAKEAWAY'}
                          >
                            Para llevar
                          </button>
                        </div>
                      ),
                      item.product.additions?.enabled || item.product.exceptions?.enabled ? (
                        <button
                          key="options"
                          type="button"
                          onClick={() => setOptionsKey(key)}
                          className="text-[11px] font-semibold text-[color:var(--color-text)] underline"
                          aria-label="Editar opciones"
                        >
                          Opciones
                        </button>
                      ) : null,
                    ].filter(Boolean)}
                    detailContent={buildDetailContentFromCartItem(item)}
                    onIncrease={() => changeQty(key, 1)}
                    onDecrease={() => changeQty(key, -1)}
                    onRemove={() => removeCartItem(key)}
                    disableDecrease={item.qty <= 1}
                  />
                  {/* Note per item */}
                  <div className="pl-1 pt-1">
                    <button
                      type="button"
                      onClick={() => setOpenNoteKey(openNoteKey === key ? null : key)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                        item.note
                          ? 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-divider text-text-muted hover:bg-surface-muted'
                      }`}
                      aria-label={item.note ? 'Editar nota' : 'Añadir nota'}
                    >
                      <MessageSquare size={11} />
                      {item.note ? 'Nota: ' + item.note.slice(0, 20) + (item.note.length > 20 ? '…' : '') : 'Nota'}
                    </button>
                    {openNoteKey === key && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <input
                          type="text"
                          autoFocus
                          value={item.note ?? ''}
                          onChange={e => setItemNote(key, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setOpenNoteKey(null); }}
                          placeholder="Ej: sin sal, extra picante…"
                          maxLength={120}
                          className="flex-1 rounded-xl border border-divider bg-surface px-3 py-1.5 text-xs text-text placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                        />
                        <button
                          type="button"
                          onClick={() => setOpenNoteKey(null)}
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          OK
                        </button>
                        {item.note && (
                          <button
                            type="button"
                            onClick={() => { setItemNote(key, ''); setOpenNoteKey(null); }}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Quitar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {totals && (
            <OrderSummaryFooter
              subtotal={totals.subtotal}
              total={totals.total}
              className="mt-3"
            />
          )}
          <div className="flex flex-col gap-2 pt-2">
            <button
              onClick={() => setCart({})}
              className="btn btn-outline w-full"
              aria-label="Vaciar pedido"
            >
              Vaciar pedido
            </button>
            <button
              onClick={handleOrder}
              disabled={hasFlavorErrors || createOrderIsPending}
              className="btn btn-primary w-full"
              aria-label="Ordenar"
            >
              <HandPlatter size={18} aria-hidden="true" />
              {createOrderIsPending ? 'Creando...' : 'Ordenar'}
            </button>
          </div>
          {hasFlavorErrors && (
            <p className="text-center text-xs text-red-500">
              Selecciona al menos un sabor para los productos requeridos.
            </p>
          )}
        </section>
      ) : null}

      {!hasActiveOrder && !hasDraft ? (
        <p className="rounded-2xl border border-dashed border-divider px-4 py-6 text-center text-sm text-muted">
          Agrega productos para crear una orden nueva.
        </p>
      ) : null}

      {/* Modales de Dividir / Cancelar (controlados por el panel) */}
      {splitModalOpen && (
        <SplitOrderModal
          open={splitModalOpen}
          order={activeOrder}
          loading={splitOrderMutation?.isPending}
          onClose={() => setSplitModalOpen(false)}
          onConfirm={(itemIds: any) => {
            if (activeOrder) splitOrderMutation?.mutate({ orderId: activeOrder.id, itemIds });
            setSplitModalOpen(false);
          }}
        />
      )}

      {cancelModalOpen && (
        <ConfirmCancelOrderModal
          open={cancelModalOpen}
          order={activeOrder}
          loading={cancelOrderMutation?.isPending}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={() => {
            handleCancelActiveOrder();
            setCancelModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
